//  A N I M A T I O N

// Driven by data-animation on sections and wrappers. The element carrying the attribute is the trigger;
// what moves inside it is found by fade_up_targets() below. Backgrounds (fills, full-bleed images and
// videos) sit on the section itself or are absolutely positioned, so they never move

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

var fade_up = {
    distance: 40,// px travelled upwards
    duration: 0.8,
    stagger: 0.15,
    stagger_pair: 0.3,// between the two items of a two-column row, where the eye has further to travel
    ease: 'power2.out',
    start: 'top 80%'// target top passes this point in the viewport
};

// ScrollSmoother speeds by data-parallax value, desktop only except the cover layers (see init_scroll_effects). Above 1 climbs faster than the page.
// clamp() starts in-view elements at their natural position
var parallax = {
    'work-card-small': 'clamp(1.2)',
    'work-card-large': 'clamp(1.04)',
    'article-text': 'clamp(1.1)',// article list text climbs past its image
    'background': 'clamp(0.85)',// full-bleed section backgrounds, oversized in CSS to cover the drift
    // Halfway between the cover text (page speed) and its background. .work-chevron is rotated 180deg, which flips
    // this drift, so 1.075 reads on screen as 0.925
    'cover-chevron': 'clamp(1.075)',
    'image': 'auto'// oversized image in a clipped frame, drifts through its overflow
};

var page_leave = {
    duration: 0.3,
    ease: 'power1.in'
};

var motion_ok = '(prefers-reduced-motion: no-preference)';

// Column by column: the grid columns of each container inside the trigger, plus anything marked
// data-animation-item, in document order. Marked items split the column they sit in, so the column
// steps aside for them; a marked item inside another marked item is left to its parent. Card lists
// and FAQs split their column too.
// Tilt elements are never targets, as VanillaTilt owns their transform. Without any of these, the
// trigger's own children stand in, skipping absolutely positioned layers such as background images
function fade_up_targets(trigger) {
    var marked = Array.prototype.slice.call(trigger.querySelectorAll('[data-animation-item]'));
    var columns = Array.prototype.slice.call(trigger.querySelectorAll(':scope > .container > .px-grid, :scope > .row > .px-grid, :scope > .px-grid'));
    columns = columns.filter(function(column) {
        return !marked.some(function(item) { return column.contains(item); });
    });
    // Lists that fade item by item (card lists left to right along each row, FAQs top to bottom): a
    // column holding one breaks into its children with the list expanded into its items
    var lists = 'ul.cards, .faqs';
    var expanded = [];
    columns.forEach(function(column) {
        if (!column.querySelector(':scope > :is(' + lists + ')')) return expanded.push(column);
        Array.prototype.forEach.call(column.children, function(child) {
            if (child.matches(lists)) {
                Array.prototype.push.apply(expanded, child.children);
            } else {
                expanded.push(child);
            }
        });
    });
    var found = expanded.concat(marked);
    var fallback = !found.length;
    if (fallback) {
        found = Array.prototype.slice.call(trigger.children);
    }
    var targets = found.filter(function(el) {
        if (el.hasAttribute('data-tilt')) return false;
        if (fallback && getComputedStyle(el).position === 'absolute') return false;
        var parent = el.parentElement;
        while (parent && parent !== trigger) {
            if (found.indexOf(parent) !== -1) return false;
            parent = parent.parentElement;
        }
        return true;
    });
    // Document order, then visual order where CSS order rearranges siblings
    targets.sort(function(a, b) {
        var order = parseInt(getComputedStyle(a).order) - parseInt(getComputedStyle(b).order);
        if (order) return order;
        return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
    return targets;
}

// ScrollSmoother owns the transform of parallax elements, so those slide via top instead
function slide(target, value) {
    return target.hasAttribute('data-parallax') ? { top: value } : { y: value };
}

// The scope itself when it matches, plus matching descendants
function query(scope, selector) {
    var matches = Array.prototype.slice.call(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) matches.unshift(scope);
    return matches;
}

// True when the element has size and is not clipped by an overflow-hidden ancestor, e.g. a hidden nav group
// Stops at the nearest nav or section so the smooth scroll wrapper does not count below-fold content as clipped
function is_shown(el) {
    var rect = el.getBoundingClientRect();
    if (!rect.height || !rect.width) return false;
    var boundary = el.closest('nav, section') || document.body;
    var ancestor = el.parentElement;
    while (ancestor) {
        if (getComputedStyle(ancestor).overflowY !== 'visible') {
            var clip = ancestor.getBoundingClientRect();
            if (rect.top >= clip.bottom || rect.bottom <= clip.top) return false;
        }
        if (ancestor === boundary) break;
        ancestor = ancestor.parentElement;
    }
    return true;
}

// Above the fold: same fade, played once the page is shown rather than on scroll
// Inline styles are cleared afterwards so CSS states keep control of the elements
function fade_on_load(container) {
    var targets = fade_up_targets(container).filter(is_shown);
    if (!targets.length) return;
    targets.forEach(function(target) {
        gsap.set(target, Object.assign({ autoAlpha: 0 }, slide(target, fade_up.distance)));
    });
    var timeline = gsap.timeline({ paused: true });
    targets.forEach(function(target, i) {
        timeline.to(target, Object.assign({
            autoAlpha: 1,
            duration: fade_up.duration,
            ease: fade_up.ease,
            // Parallax targets fade on top (see slide), so their transform belongs to ScrollSmoother and is left alone
            clearProps: target.hasAttribute('data-parallax') ? 'opacity,visibility,top' : 'opacity,visibility,transform,top'
        }, slide(target, 0)), i * fade_up.stagger);
    });
    return timeline;
}

// On scroll. Each target has its own trigger, and targets arriving in the same moment (a row of tiles,
// two columns) are batched and staggered, so long grids fade in row by row rather than all at once.
// Scrolling back above a target hides it again, so it replays on the way down. Anything already scrolled
// past when this runs, e.g. after a reload restores the scroll position, is left visible
function fade_on_scroll(section) {
    var targets = fade_up_targets(section).filter(function(target) {
        return target.getBoundingClientRect().bottom >= 0;
    });
    if (!targets.length) return;
    targets.forEach(function(target) {
        gsap.set(target, Object.assign({ autoAlpha: 0 }, slide(target, fade_up.distance)));
    });
    // Items arriving together are staggered in order. The second item of a two-column row gets the
    // longer pair gap; on mobile the columns stack and arrive separately, so they fade one under the other
    var stagger = function(group) {
        var rows = [];
        group.forEach(function(el) {
            var top = el.getBoundingClientRect().top;
            var row = rows.find(function(r) { return Math.abs(r.top - top) < 8; });
            row ? row.items.push(el) : rows.push({ top: top, items: [el] });
        });
        var delays = [];
        group.forEach(function(el, i) {
            var row = rows.find(function(r) { return r.items.indexOf(el) > 0; });
            var pair = row && row.items.length === 2;
            delays.push(i ? delays[i - 1] + (pair ? fade_up.stagger_pair : fade_up.stagger) : 0);
        });
        return function(i) { return delays[i]; };
    };
    // Parallax targets slide via top, the rest via y, so each batch tweens in two groups
    var tween = function(batch, shown) {
        [true, false].forEach(function(parallax) {
            var group = batch.filter(function(el) { return el.hasAttribute('data-parallax') === parallax; });
            if (!group.length) return;
            gsap.to(group, Object.assign({
                autoAlpha: shown ? 1 : 0,
                duration: shown ? fade_up.duration : fade_up.duration / 2,
                stagger: shown ? stagger(group) : 0,
                ease: fade_up.ease,
                overwrite: true
            }, slide(group[0], shown ? 0 : fade_up.distance)));
        });
    };
    ScrollTrigger.batch(targets, {
        start: fade_up.start,
        onEnter: function(batch) { tween(batch, true); },
        onLeaveBack: function(batch) { tween(batch, false); }
    });
}

// Headings that drift sideways as they pass through the viewport
function moving_heads(scope) {
    query(scope, '.moving-head').forEach(function(head) {
        var slower = !!head.closest('.slower');
        gsap.fromTo(head, { x: '20%' }, {
            x: slower ? '-20%' : '-100%',
            ease: 'none',
            scrollTrigger: {
                trigger: head,
                scrub: 1,
                start: 'top bottom',
                end: 'bottom top'
            }
        });
    });
}

// Hidden states are set here, not in CSS, so content stays visible without JS or with reduced motion.
// Returns a function that plays the on-load fades, for the caller to run once the page is revealed
export function init_animations(scope) {
    scope = scope || document;
    var timelines = [];
    gsap.matchMedia().add(motion_ok, function() {
        query(scope, '[data-animation="fade-up-on-load"]').forEach(function(container) {
            var timeline = fade_on_load(container);
            if (timeline) timelines.push(timeline);
        });
        query(scope, '[data-animation="fade-up-on-scroll"]').forEach(fade_on_scroll);
    });
    return function() {
        timelines.forEach(function(timeline) { timeline.play(); });
    };
}

// Parallax and moving headings are desktop only, as the tiles stack on mobile and the headings run out of room.
// Full-bleed backgrounds (covers, text-in-image blocks, quote images) and the cover chevron are the exception
// and drift at every width
var all_widths = ['background', 'cover-chevron'];

export function init_scroll_effects(scope) {
    scope = scope || document;
    var smoother = ScrollSmoother.get();
    var media = gsap.matchMedia();
    function apply(names) {
        names.forEach(function(name) {
            var elements = query(scope, '[data-parallax="' + name + '"]');
            if (elements.length) smoother.effects(elements, { speed: parallax[name] });
        });
    }
    media.add(motion_ok, function() {
        if (smoother) apply(all_widths);
    });
    media.add('(min-width: 768px) and ' + motion_ok, function() {
        if (smoother) apply(Object.keys(parallax).filter(function(name) { return all_widths.indexOf(name) === -1; }));
        moving_heads(scope);
    });
}

// Content appended after load, e.g. paginated tiles
export function animate_added(el) {
    init_animations(el)();
    init_scroll_effects(el);
}

// Fade the page out before following an internal link. Links that open elsewhere, download,
// jump within the page or carry a modifier key are left to the browser
export function init_page_leave() {
    var content = document.getElementById('smooth-content');
    if (!content) return;
    document.addEventListener('click', function(event) {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (!window.matchMedia(motion_ok).matches) return;
        var link = event.target.closest('a[href]');
        if (!link || link.target || link.hasAttribute('download')) return;
        if (link.origin !== location.origin) return;
        if (link.pathname === location.pathname && link.search === location.search && link.hash) return;
        event.preventDefault();
        gsap.to(content, {
            autoAlpha: 0,
            duration: page_leave.duration,
            ease: page_leave.ease,
            onComplete: function() { location.href = link.href; }
        });
    });
    // Back/forward cache restores the faded page as it was left
    window.addEventListener('pageshow', function(event) {
        if (event.persisted) gsap.set(content, { clearProps: 'opacity,visibility' });
    });
}
